import { alpha } from '@mui/material/styles';
import {
  blueGrey,
  grey,
  primary,
  secondary,
} from '../../../../utils/brandColors';

const getActiveMode = (palette) => {
  if (palette?.mode) {
    return palette.mode;
  }
  if (typeof document !== 'undefined') {
    return document.documentElement.getAttribute('data-mui-color-scheme') || 'light';
  }
  return 'light';
};

const resolveColor = (value, fallbackLight, fallbackDark, mode) => {
  if (value && typeof value === 'string' && !value.startsWith('var(')) {
    return value;
  }
  if (value && typeof value === 'string' && value.startsWith('var(')) {
    return mode === 'light'
      ? fallbackLight
      : fallbackDark;
  }
  return mode === 'light'
    ? fallbackLight
    : fallbackDark;
};

const importantify = (value) => {
  if (value == null) return value;
  if (typeof value === 'string') {
    return value.endsWith('!important')
      ? value
      : `${value} !important`;
  }
  if (Array.isArray(value)) {
    return value.map((item) => importantify(item));
  }
  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = importantify(value[key]);
      return acc;
    }, {});
  }
  return value;
};

const buildStyles = (incomingTheme, explicitMode) => {
  const theme = incomingTheme || {};
  const palette = theme.vars?.palette || theme.palette || {};
  const mode = explicitMode || getActiveMode(palette);
  const dividerColor = resolveColor(palette.divider, grey[300], '#2f2f2f', mode);
  const surfaceColor = resolveColor(palette.background?.paper, '#ffffff', '#1c1c1c', mode);
  const hoverColor = mode === 'light'
    ? alpha(primary[500], 0.08)
    : alpha(primary[200], 0.2);
  const subtleSurface = mode === 'light'
    ? blueGrey[50]
    : alpha('#ffffff', 0.06);
  const defaultBackground = resolveColor(palette.background?.default, '#f6f6f6', '#050505', mode);
  const stickyBackground = mode === 'light'
    ? alpha('#ffffff', 0.95)
    : alpha(defaultBackground, 0.92);
  const shadows = theme.shadows || [];

  const baseCell = {
    verticalAlign: 'top',
    borderRight: `1px solid ${dividerColor}`,
    maxWidth: '250px',
    minWidth: '250px',
    padding: 0,
    cursor: 'pointer',
    backgroundColor: surfaceColor,
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': { backgroundColor: hoverColor },
  };

  const styles = {
    paperRoot: {
      overflow: 'auto',
      marginTop: '1rem',
      borderRadius: '12px',
      backgroundColor: surfaceColor,
      border: `1px solid ${alpha(dividerColor, 0.6)}`,
      boxShadow: shadows[mode === 'light'
        ? 2
        : 8] || 'none',
    },
    rowLocation: {
      borderBottom: `6px solid ${primary[500]}`,
      borderLeft: `4px solid ${alpha(primary[500], 0.5)}`,
      backgroundColor: subtleSurface,
    },
    row: { borderBottom: `2px solid ${alpha(dividerColor, 0.8)}` },
    cell: { ...baseCell },
    cellPast: {
      ...baseCell,
      backgroundColor: subtleSurface,
      '&:hover': { backgroundColor: alpha(hoverColor, 0.5) },
    },
    cellNotLocated: {
      ...baseCell,
      backgroundColor: alpha(secondary[700], mode === 'light'
        ? 0.15
        : 0.35),
      color: palette.text?.primary || '#fff',
      '&:hover': {
        backgroundColor: alpha(secondary[700], mode === 'light'
          ? 0.25
          : 0.5),
      },
    },
    cellSticky: {
      ...baseCell,
      borderRight: `1px solid ${alpha(dividerColor, 0.8)}`,
      maxWidth: '200px',
      minWidth: '200px',
      width: '200px',
      padding: '6px',
      position: 'sticky',
      left: 50,
      backgroundColor: stickyBackground,
      backdropFilter: 'blur(6px)',
      zIndex: '100',
      '&:hover': { backgroundColor: stickyBackground },
    },
    cellStickyNarrow: {
      ...baseCell,
      borderRight: `1px solid ${alpha(dividerColor, 0.8)}`,
      maxWidth: '50px',
      minWidth: '50px',
      width: '50px',
      padding: '6px 4px',
      position: 'sticky',
      left: 0,
      backgroundColor: stickyBackground,
      backdropFilter: 'blur(6px)',
      zIndex: '101',
      '&:hover': { backgroundColor: stickyBackground },
    },
    cellHighlight: {
      ...baseCell,
      maxWidth: '400px',
      minWidth: '400px',
      // backgroundColor: subtleSurface,
    },
    cellHeadHighlight: {
      borderRight: `1px solid ${dividerColor}`,
      fontSize: 16,
      fontWeight: 600,
      textAlign: 'center',
      backgroundColor: mode === 'light'
        ? blueGrey[500]
        : blueGrey[700],
      color: blueGrey[50],
      cursor: 'pointer',
    },
    cellHead: {
      borderRight: `1px solid ${dividerColor}`,
      fontSize: 16,
      fontWeight: 600,
      textAlign: 'center',
      backgroundColor: subtleSurface,
      color: palette.text?.primary || '#111',
      cursor: 'pointer',
    },
    cellHeadStickyNarrow: {
      fontSize: 16,
      fontWeight: 600,
      textAlign: 'center',
      maxWidth: '50px',
      minWidth: '50px',
      width: '50px',
      padding: '6px 4px',
      backgroundColor: `${stickyBackground} !important`,
      color: palette.text?.primary || '#111',
      position: 'sticky',
      left: 0,
      zIndex: '102 !important',
      borderRight: `1px solid ${dividerColor}`,
    },
    cellHeadSticky: {
      fontSize: 16,
      fontWeight: 600,
      textAlign: 'center',
      maxWidth: '200px',
      minWidth: '200px',
      width: '200px',
      padding: '6px 8px',
      backgroundColor: `${stickyBackground} !important`,
      color: palette.text?.primary || '#111',
      position: 'sticky',
      left: 50,
      zIndex: '102 !important',
      borderRight: `1px solid ${dividerColor}`,
      '& .MuiButton-root': {
        whiteSpace: 'normal',
        textAlign: 'left',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        lineHeight: 1.2,
        width: '100%',
      },
    },
  };

  return importantify(styles);
};

export default buildStyles;
